export const PROCARD_CONSTANTS = {
  countryISOAlpha2Code: "NL",
} as const;

export const PROCARD_FORM_STEPS = [
  "consent",
  "company-details",
  "company-list",
  "authorized-person",
  "bank-details",
  "summary",
];

export type ProCardFormStep =
  | "consent"
  | "company-details"
  | "company-list"
  | "authorized-person"
  | "bank-details"
  | "summary";

export enum ProCardApplicationType {
  WITH_CREDIT = "PROCARD",
  WITHOUT_CREDIT = "PROCARD_ZERO",
}

export type PROCARDMODALDETAILS = {
  text: string;
}

const PROCARD_BANK_TYPES = ["Company", "Personal"] as const;

export type ProCardBankType = (typeof PROCARD_BANK_TYPES)[number];

export type ProCardFormSavedData = {
  id: number;
  application_data: string;
  status: number;
}

export type ProCardFormSavedDataResponse = {
  data: ProCardFormSavedData
}

export type ValidateAccountHolderNameResponse = {
  companyPrinciple: boolean;
}

export type BankDetails = {
  Bic: string;
  Name : string;
}

export type ProCardCompanyList = {
  displaySequence: number;
  organization: Organization;
  matchQualityInformation: MatchQualityInformation;
};

export type DunsControlStatus = {
  operatingStatus: OperatingStatus;
  isMailUndeliverable: boolean;
};

export type OperatingStatus = {
  description: string;
  dnbCode: number;
};

export type Telephone = {
  telephoneNumber: string;
  isUnreachable: boolean;
};

export type Country = {
  isoAlpha2Code: string;
  name: string;
};

export type Locality = {
  name: string;
};

export type StreetAddress = {
  line1: string;
  line2: string | null;
};


export type Principal = {
  fullName: string;
};

export type CorporateLinkage = {
  familytreeRolesPlayed: FamilytreeRole[];
};

export type FamilytreeRole = {
  description: string;
  dnbCode: number;
};

export type MatchQualityInformation = {
  confidenceCode: number;
  matchGrade: string;
  matchGradeComponentsCount: number;
  matchGradeComponents: MatchComponentRating[];
  matchDataProfile: string;
  matchDataProfileComponentsCount: number;
  matchDataProfileComponents: MatchComponentValue[];
  nameMatchScore: number;
};

export type MatchComponentRating = {
  componentType: string;
  componentRating: string;
};

export type MatchComponentValue = {
  componentType: string;
  componentValue: string;
};


export type ProCardFormData = {
  verify_age: boolean;
  terms_accepted: boolean;
  company_name: string;
  company_duns: string;
  company_address_line1: string;
  company_address_city: string;
  company_country: string;
  company_postcode: string;
  company_registration_number: string;
  account_holder_name: string;
  phone_number: string;
  company_country_name: string;
  business_desc: string;
  [key: string]: any;
};

export type ProCardApplicationStatus =
  | "draft"
  | "active"
  | "processed"
  | "failed";

export const ProCardActiveStatuses = ["active" ,"processed"];

export const PROCARD_STATUS_LIST = [
  "ok",
  "pending",
  "failed",
  "cancel",
  "print",
] as const;

export type ProCardErrorResponse = {
  error: {
    code: string;
    message: string;
    info: string;
    data: {
      message: string;
    };
  };
}

export type ProCardPostApplyStatus = (typeof PROCARD_STATUS_LIST)[number];

export type CompanyInfoResponse = {
  transactionDetail: TransactionDetail;
  inquiryDetail: InquiryDetail;
  organization: Organization;
  blockStatus: BlockStatus[];
};

export type TransactionDetail = {
  transactionID: string;
  transactionTimestamp: string;
  inLanguage: string;
};

export type InquiryDetail = {
  duns: string;
  blockIDs: string[];
};

export type BlockStatus = {
  blockID: string;
  status: string;
  reason: string | null;
};

export type Organization = {
  duns: string;
  isFortune1000Listed: boolean | null;
  isForbesLargestPrivateCompaniesListed: boolean | null;
  industryCodes: IndustryCode[];
  primaryIndustryCode: {
    usSicV4: string;
    usSicV4Description: string;
  };
  unspscCodes: UNSPSCCode[];
  primaryName: string;
  tradeStyleNames: TradeStyleName[];
  registeredAddress: Address;
  primaryAddress: Address;
  mailingAddress: Address;
  telephone: Telephone[];
  legalForm: LegalForm;
  businessEntityType: {
    description: string;
    dnbCode: number;
  };
  organizationSizeCategory?: {
    description: string;
    dnbCode: number;
  };
  websiteAddress: string[];
  registeredName: string;
  countryISOAlpha2Code: string;
  isImporter: boolean;
  isExporter: boolean;
  startDate: string;
  incorporatedDate: string;
  activities: Activity[];
  defaultCurrency: string;
  registrationNumbers: RegistrationNumber[];
  financials: Financial[];
};

export type IndustryCode = {
  code: string;
  description: string;
  typeDescription: string;
  typeDnBCode: number;
  priority: number;
};

export type UNSPSCCode = {
  code: string;
  description: string;
  priority: number;
  override?: boolean;
};

export type TradeStyleName = {
  name: string;
  priority: number;
};

export type Address = {
  language: {
    description: string;
    dnbCode: number;
  };
  addressCountry: {
    name: string;
    isoAlpha2Code: string;
  };
  addressLocality: {
    name: string;
  };
  addressRegion: {
    name: string;
    abbreviatedName?: string | null;
    isoSubDivisionName?: string;
    isoSubDivisionCode?: string;
  };
  streetName?: string;
  streetNumber?: string | null;
  streetAddress?: {
    line1: string | null;
    line2: string | null;
  };
  postOfficeBox?: {
    postOfficeBoxNumber?: string;
    typeDescription?: string;
    typeDnBCode?: number;
  };
  postalCode: string;
};

export type LegalForm = {
  description: string;
  dnbCode: number;
  startDate?: string;
};

export type Activity = {
  description: string;
  language: {
    description: string;
    dnbCode: number;
  };
};

export type RegistrationNumber = {
  registrationNumber: string;
  typeDescription: string;
  typeDnBCode: number;
  registrationNumberClass: {
    description: string;
    dnbCode: number;
  };
  isPreferredRegistrationNumber: boolean | null;
};

export type Financial = {
  financialStatementToDate: string;
  informationScopeDescription: string;
  informationScopeDnBCode: number;
  reliabilityDescription: string;
  reliabilityDnBCode: number;
  unitCode: string;
  yearlyRevenue: Revenue[];
};

export type Revenue = {
  value: number;
  currency: string;
  minimumValue?: number | null;
  maximumValue?: number | null;
};

export const typeDnBCodesData = {
  VAT: 6273,
  RegistrationNumbers: {
    NL: [6256],
    BE: [800],
  },
};

export type ProCardDraftResponse = {
  data: {
    id: number;
    application_data: string;
    status: number;
  };
};

